import {
  FileWithStatus,
  FloorplannerImportState,
  ImporterFeedback,
  TwinLifecycleStageValues,
} from '@repo/frontend-types';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

import { ReactComponent as AddPlan } from '@repo/assets/icons/add-plan.svg';
import { ReactComponent as PlanIcon } from '@repo/assets/icons/plan.svg';
import loadingGif from '@repo/assets/loading.gif';

import { MessageBar, MessageBarIntent } from '@fluentui/react-components';
import { AddSensorInput, MetricDataType, MetricTypeRef } from '@repo/backend-types';
import { useUiSettingsStore, useUserStore, useTwinContext } from '@repo/front-end-state';
import type { UiSettingsStore } from '@repo/front-end-state';
import { calcPercent } from '@repo/front-end-utils';
import { ProgressInfo } from '@repo/front-end-utils';
import { ParsedInventory, parseInventory } from '@repo/ui';
import { FloorPlannerLayout, formatFmlValidationErrors, validateFML } from '@repo/ui';
import { modelManager } from '@repo/ui';
import { parseLatLong, parseOrientation, TLSpinner, DialogForm } from '@repo/ui';
import { Button } from '@repo/design-system';
interface Props {
  className?: string;
  onClose: () => void;
  hierarchyString: string;
  file: FileWithStatus;
  setFile: (file: FileWithStatus) => void;
  setSensorInputs: (sensorInputs: AddSensorInput[]) => void;
  addImporterFeedback: (feedback: ImporterFeedback, clear?: boolean) => void;
  onLifecycleStageUpdate?: (stage: string) => void;
}

const storeSelector = (store: UiSettingsStore) => ({
  //     analyzeScene: store.analyzeScene,
  directedPlaceholderGeometry: store.directedPlaceholderGeometry,
});

const AddFloorplannerFile_: React.FC<Props> = ({
  className,
  onClose,
  hierarchyString,
  file,
  setFile,
  setSensorInputs,
  addImporterFeedback,
  onLifecycleStageUpdate,
}) => {
  const { authMetadata } = useUserStore();
  const { twin, setTwin, twinBId } = useTwinContext();

  const { directedPlaceholderGeometry } = useUiSettingsStore(storeSelector);

  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [parsedInventory, setParsedInventory] = useState<ParsedInventory | null>(null);

  const fileInputRef = useRef<HTMLInputElement>(null);
  const validationReportRef = useRef<HTMLPreElement>(null);
  const [outputMessage, setOutputMessage] = useState<{
    message: string;
    intent: MessageBarIntent;
    allowDownload?: boolean;
    suggestedFilename?: string;
    shouldScroll?: boolean;
  } | null>(null);

  const progress = useRef<ProgressInfo | null>(null);
  const [renderCounter, setRenderCounter] = useState<number>(0);

  const [currentState, setCurrentState] = useState<FloorplannerImportState>('awaiting-fml');
  const [isUpdatingLifecycle, setIsUpdatingLifecycle] = useState<boolean>(false);

  // Consider the existing dgraph physical model "broken" if it has no children
  // This allows users to proceed even when the current saved model is unusable
  const isPhysicalModelBroken: boolean =
    !twin?.physicalModel?.children || twin.physicalModel.children.length === 0;

  const validateFileExtension = (file: File) => {
    const validExtensions = ['json', 'fml'];
    const extension = file.name.toLowerCase().split('.').pop() ?? '';

    const valid = validExtensions.includes(extension);

    return {
      valid,
      message: `Invalid file extension: ${extension}.\nValid extensions are: ${validExtensions.join(', ')}`,
    };
  };

  const progressCallback = useCallback((pi: ProgressInfo) => {
    progress.current = pi;
    setOutputMessage({
      message: progress.current.messages.join(`\n`),
      intent: 'info',
      allowDownload: false,
      shouldScroll: true,
    });
    setRenderCounter(prev => prev + 1);
  }, []);

  const doParseFloorplannerLayout = useCallback(
    (floorPlannerLayout: FloorPlannerLayout) => {
      const doParse = async () => {
        if (twin && authMetadata) {
          const { orgBID } = authMetadata;

          // Get the physical model, or use fallback values if it's broken/missing
          const physicalModel = twin.physicalModel;
          const name = physicalModel?.name || 'Site';
          const bID = physicalModel?.bID || '';

          // Use mapOrigin from physical model, or fallback to adminTwin location if physical model is broken
          let mapOrigin: { latitude: number; longitude: number } | undefined =
            physicalModel?.mapOrigin
              ? {
                  latitude: physicalModel.mapOrigin.latitude,
                  longitude: physicalModel.mapOrigin.longitude,
                }
              : undefined;
          if (!mapOrigin && twin.adminTwin) {
            mapOrigin = {
              latitude: parseFloat(twin.adminTwin.latitude) || 0,
              longitude: parseFloat(twin.adminTwin.longitude) || 0,
            };
          }

          if (orgBID && mapOrigin) {
            setCurrentState('importing-fml');

            setIsLoading(true);
            queueMicrotask(async () => {
              try {
                const pi = await parseInventory({
                  fml: floorPlannerLayout,
                  siteName: name,
                  twinBID: bID,
                  mapOrigin,
                  orgBID,
                  prefixBid: false,
                  hierarchyString,
                  progressCallback,
                });
                setParsedInventory(pi);

                // Don't override the validation report - let it show the detailed import information
                setIsLoading(false);
              } catch (error) {
                console.error('Error during FML parsing:', error);
                setIsLoading(false);
                addImporterFeedback(
                  {
                    intent: 'error',
                    message: `FML parsing failed: ${error instanceof Error ? error.message : String(error)}`,
                  },
                  true,
                );
                setOutputMessage({
                  message: `FML parsing failed: ${error instanceof Error ? error.message : String(error)}`,
                  intent: 'error',
                  allowDownload: false,
                });
              }
            });
          }
        }
      };
      doParse();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- keyed on the parse inputs; `addImporterFeedback` prop is only called on error
    [
      authMetadata,
      file.file?.name,
      hierarchyString,
      progressCallback,
      setCurrentState,
      setParsedInventory,
      twin,
    ],
  );

  const handleFileChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      if (e.target.files) {
        const targetFile = e.target.files[0];

        const { valid, message } = validateFileExtension(targetFile);

        if (valid) {
          setFile({
            file: targetFile,
            status: 'valid-extension',
          });
          targetFile.text().then(async text => {
            let layout: FloorPlannerLayout;
            try {
              setIsLoading(true);
              queueMicrotask(() => {
                layout = JSON.parse(text) as FloorPlannerLayout;
                setIsLoading(false);
              });
            } catch (error) {
              setIsLoading(false);
              console.error(
                `Error parsing JSON: ${error instanceof Error ? error.message : String(error)}`,
                error,
              );
              setOutputMessage({
                message: `Error parsing JSON`,
                intent: 'error',
                allowDownload: false,
              });
              setFile({
                file: targetFile,
                status: 'invalid-json',
              });
              return;
            }

            setIsLoading(true);
            queueMicrotask(async () => {
              const result = await validateFML(
                layout,
                hierarchyString, // TODO: make this configurable (e.g. twinPreferences)
                {
                  bID: twinBId!,
                },
              );
              setIsLoading(false);

              if ('errors' in result) {
                addImporterFeedback(
                  {
                    intent: 'error',
                    message: 'Floorplanner file failed to import.',
                  },
                  true,
                );

                setOutputMessage({
                  message: formatFmlValidationErrors(result.errors),
                  intent: 'error',
                  suggestedFilename: `${
                    file.file?.name ? `${file.file?.name} - ` : ''
                  }import errors.txt`,
                  allowDownload: true,
                });
                setFile({
                  file: targetFile,
                  status: 'invalid-fml',
                });
              } else {
                setFile({
                  file: targetFile,
                  status: 'valid-fml',
                });
                setCurrentState('importing-fml');
                queueMicrotask(async () => {
                  doParseFloorplannerLayout(result);

                  setOutputMessage({
                    message: JSON.stringify(result, null, 2),
                    intent: 'info',
                    suggestedFilename: `${
                      file.file?.name ? `${file.file?.name} - ` : ''
                    }import report.txt`,
                    allowDownload: true,
                  });
                });

                // clear errors
                addImporterFeedback(
                  {
                    message: 'Floorplanner file imported successfully',
                    intent: 'success',
                  },
                  true,
                );
              }
            });
          });
        } else {
          setOutputMessage({
            message,
            intent: 'error',
          });
          setFile({
            file: targetFile,
            status: 'invalid-extension',
          });
        }
      }
    },
    [
      doParseFloorplannerLayout,
      file.file?.name,
      hierarchyString,
      setCurrentState,
      setFile,
      addImporterFeedback,
      twinBId,
    ],
  );

  useEffect(() => {
    if (progress.current) {
      if (
        currentState === 'importing-fml' &&
        progress.current.total === progress.current.completed
      ) {
        setCurrentState('awaiting-add-to-map');
      }
    }
  }, [currentState, renderCounter, setCurrentState]);

  useEffect(() => {
    if (validationReportRef.current && outputMessage?.shouldScroll) {
      validationReportRef.current.scrollTop = validationReportRef.current.scrollHeight;
    }
  }, [outputMessage]);

  const addPhysicalModelAndUpdateLifecycle = useCallback(async () => {
    if (!parsedInventory || !twin) {
      return;
    }

    const { entities, twin: physicalModel } = parsedInventory;

    // Super-hack - copy the position and orientation from the admin twin
    // into the SITE in the physical model
    // as admin-db is now the source of truth
    if (twin.adminTwin) {
      physicalModel.rotationY = parseOrientation(twin.adminTwin.orientation) ?? 0;
      physicalModel.mapOrigin = parseLatLong(twin.adminTwin);
    }
    // TB Note: Implemented by Lee originally in the parseTwin

    modelManager.reinitialize({
      twin: physicalModel,
      authMetadata,
      directedPlaceholderGeometry,
    });

    const metricType: MetricTypeRef = {
      name: 'count',
      description: 'Quantities of things',
      metrics: [
        {
          name: 'countEntity',
          description: 'Number of objects counted in the entity',
          unit: 'count',
          dataType: MetricDataType.Int,
        },
        {
          name: 'capacity',
          description: 'Entity capacity',
          unit: 'count',
          dataType: MetricDataType.Int,
        },
        {
          name: 'usage',
          description: 'Count/Capacity expressed as percentage',
          unit: '%',
          dataType: MetricDataType.Float,
        },
      ],
    };

    // Only create sensors if there are entities
    if (entities && entities.length > 0) {
      const sensorInputs = entities.map((entity): AddSensorInput => {
        return {
          bID: entity.bID,
          name: `PeopleCountLive (${entity.bID})`,
          metricType,
          maker: 'Nola',
          modelName: 'PeopleCountLive',
          entity: {
            bID: entity.bID,
          },
        };
      });
      setSensorInputs(sensorInputs);
    } else {
      console.warn('No entities found in parsed inventory, cannot create sensors');
      setSensorInputs([]);
    }

    // Update lifecycle stage in the backend
    if (authMetadata?.tokens?.accessToken && twinBId) {
      const { updateTwin } = await import('@repo/front-end-services');
      const Config = (await import('../../common/Config')).default;
      await updateTwin(Config.middlewareApi, authMetadata.tokens.accessToken, twinBId, {
        lifecycle_stage: TwinLifecycleStageValues.ThreeDScene,
      });
    }

    // Update local twin state with both physical model and lifecycle stage in one call
    setTwin({
      ...twin,
      //@ts-ignore - it thinks ID is required but it's not
      physicalModel,
      totalCapacity: physicalModel.capacity!,
      ...(twin.adminTwin && {
        adminTwin: {
          ...twin.adminTwin,
          lifecycle_stage: TwinLifecycleStageValues.ThreeDScene,
        },
      }),
    });

    // Notify parent component to update form field
    onLifecycleStageUpdate?.(TwinLifecycleStageValues.ThreeDScene);
  }, [
    authMetadata,
    directedPlaceholderGeometry,
    onLifecycleStageUpdate,
    parsedInventory,
    setSensorInputs,
    setTwin,
    twin,
    twinBId,
  ]);

  return !!twinBId ? (
    <div className={className}>
      <DialogForm className="wider-dialog" icon={<AddPlan />} header="Add Plan" onClose={onClose}>
        {
          <>
            <fieldset>
              <div className="upload-button">
                <label htmlFor="file-upload" className="file-upload-label">
                  <span className="visually-hidden">Choose floorplanner file (.json or .fml)</span>
                  <input
                    id="file-upload"
                    ref={fileInputRef}
                    type="file"
                    accept=".json,.fml"
                    className="sr-only-file-input"
                    onChange={e => {
                      handleFileChange(e);
                    }}
                  />
                </label>
                <Button
                  variant="frosted"
                  iconLeft={<PlanIcon />}
                  onClick={() => {
                    // Reset file state
                    setFile({
                      file: null,
                      status: 'unset',
                    });
                    // Clear the input value to allow selecting the same file again
                    if (fileInputRef.current) {
                      fileInputRef.current.value = '';
                      // Trigger the file input click
                      fileInputRef.current.click();
                    }
                  }}
                >
                  Choose file
                </Button>
              </div>

              {!!progress.current && (
                <MessageBar className="message-bar" intent="info">
                  {`${calcPercent(
                    progress.current.completed,
                    progress.current.total,
                  )} complete (${progress.current.completed} of ${progress.current.total})`}
                </MessageBar>
              )}
            </fieldset>
          </>
        }
        <div className="validation-report">
          {!!outputMessage && (
            <div className="report">
              <pre id="validationReport" ref={validationReportRef}>
                {outputMessage!.message}
              </pre>
            </div>
          )}

          {!!outputMessage && outputMessage!.allowDownload && (
            <a
              href={`data:text/plain;charset=utf-8,${encodeURIComponent(outputMessage!.message)}`}
              download={outputMessage!.suggestedFilename}
              className="download-link"
            >
              Download Validation Report
            </a>
          )}
        </div>

        <div className="button-container">
          {file.status !== 'invalid-json' &&
            file.status !== 'invalid-fml' &&
            (currentState === 'awaiting-add-to-map' ||
              file.status === 'valid-fml' ||
              file.status === 'file-imported') && (
              <Button
                variant="primary"
                size="full"
                onClick={async () => {
                  try {
                    setIsUpdatingLifecycle(true);
                    await addPhysicalModelAndUpdateLifecycle();
                    onClose();
                  } catch (error) {
                    console.error('Failed to update twin lifecycle stage:', error);
                    // Still close the dialog even on error, but user will see the error in console
                    onClose();
                  } finally {
                    setIsUpdatingLifecycle(false);
                  }
                }}
                disabled={
                  currentState !== 'awaiting-add-to-map' ||
                  isUpdatingLifecycle ||
                  (!parsedInventory && !isPhysicalModelBroken)
                }
              >
                {isUpdatingLifecycle ? 'Updating...' : 'Next'}
              </Button>
            )}
        </div>
      </DialogForm>

      {isLoading && (
        <div className="loading">
          <TLSpinner custom={<img src={loadingGif} width={200} height={200} alt="Loading..." />} />
        </div>
      )}
    </div>
  ) : (
    <div>No twin BID found</div>
  );
};

export const AddFloorplannerFile = styled(AddFloorplannerFile_)`
  width: 100%;
  position: relative;
  display: flex;
  flex-direction: column;

  .wider-dialog {
    display: flex;
    flex-direction: column;
  }

  .wider-dialog .form-container {
    width: 500px;
    display: flex;
    flex-direction: column;
  }

  .upload-button {
    font-size: 1rem;
    color: #242424;
    position: relative;
    display: flex;
    justify-content: center;

    button {
      width: 250px;
      height: 53px !important;
      margin: 0 auto !important;
    }
  }

  /* Was the Tailwind sr-only utility name; renamed to a custom class to avoid the collision. */
  .visually-hidden {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .sr-only-file-input {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
    /* Keep accessible to screen readers - do not use opacity: 0 or pointerEvents: none */
  }

  .file-upload-label {
    /* Label is for screen reader association only, visually hidden */
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }

  .loading {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    background: rgba(255, 255, 255, 0.7);
    z-index: 1000;
  }

  .message-bar {
    margin: 1rem 0;
  }

  .validation-report {
    margin-bottom: 2rem;
    clear: both;
    width: 100%;
    display: block;
    order: 999; /* Force it to appear at the bottom */
  }

  .report {
    border: solid 1px #d9d9d9;
    border-radius: 8px;
    padding: 0.5rem 1rem;
    background: #f5f5f5;
    width: 0;
    min-width: 100%;
    overflow: auto;
    white-space: pre-wrap;
    word-wrap: break-word;
    box-sizing: border-box;
    margin: 0 auto 1rem auto;
    max-height: 250px;
    font-size: 0.8rem;
  }

  .button-container {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    order: 1000; /* Ensure buttons are at the very bottom */
  }

  .alt-icon-col path:nth-of-type(2) {
    fill: #00bbcc !important;
  }

  .download-link {
    display: block;
    margin-top: 8px;
    margin-bottom: 8px;
    color: #00bbcc;
    text-align: left;
    text-decoration: none;
    cursor: pointer;
  }
`;
